'use client';

import { type ReactNode, type RefObject, useRef } from 'react';
import { createContext, useContext } from 'react';
import { parseAsInteger, useQueryState } from 'nuqs';

interface PaginationState {
    page: number;
    prevPage: RefObject<number>;
    setPage: (page: number) => void;
}

const PaginationContext = createContext<PaginationState>(undefined as unknown as PaginationState);

export interface PaginationProviderProps {
    children: ReactNode;
    onPageChange?: (page: number) => void;
}

export function PaginationProvider({ children, onPageChange }: PaginationProviderProps) {
    const [page, setPage] = useQueryState('page', parseAsInteger);
    const pageInBounds = !page || page < 1 ? 1 : page;
    const prevPage = useRef(pageInBounds);

    return (
        <PaginationContext.Provider
            value={{
                page: pageInBounds,
                prevPage,
                setPage: (p: number) => {
                    setPage(p).then(() => {
                        prevPage.current = pageInBounds;
                        onPageChange?.(p);
                    });
                },
            }}
        >
            {children}
        </PaginationContext.Provider>
    );
}

export function usePaginationState() {
    const context = useContext(PaginationContext);
    if (!context) {
        throw new Error('usePaginationState must be used within a PaginationProvider');
    }

    return context;
}
